/**
 * Reference: https://netbasal.com/creating-powerful-components-with-angular-cdk-2cef53d81cea
 */

import { Injectable, Injector } from '@angular/core';
import { PositionStrategy, ConnectionPositionPair, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { PopupContent, PopupRef } from './popup.ref';
import { PortalInjector, ComponentPortal } from '@angular/cdk/portal';
import { PopupComponent } from '../components/popup/popup.component';


export enum PopupPosition {
  Above = 'above',
  Below = 'below',
  Left = 'left',
  Right = 'right'
}

const positionPairs: { [key: string]: ConnectionPositionPair} = {
  [PopupPosition.Above]: {
    originX : 'center',
    originY : 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -12
  },
  [PopupPosition.Below]: {
    originX : 'center',
    originY : 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 12
  },
  [PopupPosition.Left]: {
    originX : 'start',
    originY : 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -12
  },
  [PopupPosition.Right]: {
    originX : 'end',
    originY : 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 12
  }
};

export interface PopupParams<T> {
  origin: HTMLElement;
  content: PopupContent;
  data?: T;
  width?: string | number;
  height?: string | number;
 }

@Injectable({
  providedIn: 'root'
})
export class PopupService {
  
  constructor(
    private overlay: Overlay,
    private injector: Injector
    ) { }
  

  open<T>({ origin, content, data, width, height }: PopupParams<T>): PopupRef<T> {
    const overlayRef = this.overlay.create(this.getOverlayConfig({origin, width, height}));

    const popupRef = new PopupRef<T>(overlayRef, content, data);
    const injector = this.createInjector(popupRef, this.injector);

    overlayRef.attach(new ComponentPortal(PopupComponent, null, injector));

    return popupRef;
  }

  createInjector(popupRef: PopupRef, injector: Injector) {
    const tokens = new WeakMap([[PopupRef, popupRef]]);
    return new PortalInjector(injector, tokens);
  }

  getPositionStrategy(origin: HTMLElement, popupPositions: PopupPosition[]): PositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(this.getPositions(popupPositions));
  }

  private getOverlayConfig({ origin, width, height }): OverlayConfig {
    return new OverlayConfig({
      width,
      height,
      hasBackdrop: true,
      backdropClass: 'popup-scrim',
      positionStrategy: this.getOverlayPosition(origin),
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });
  }

  private getOverlayPosition(origin: HTMLElement, positions?: PopupPosition[]): PositionStrategy {
    positions = positions || [PopupPosition.Above, PopupPosition.Below, PopupPosition.Left, PopupPosition.Right];

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(origin)
      .withPositions(this.getPositions(positions))
      .withPush(true);
   
    return positionStrategy;
   }
   

  private getPositions(popupPositions: PopupPosition[]): ConnectionPositionPair[] {
    return popupPositions.map(position => {
      return positionPairs[position];
    });
  }
}
